/**
 * Renderers for fenced blocks a surface understands (DOP-618).
 *
 * The chat renders an answer's fenced code as code. A surface that hosts the
 * chat can claim a language and draw that block its own way. The deal
 * screening rail draws a `memo-edit` block as a before and after with Apply.
 * Outside such a surface the block stays code, so nothing depends on the
 * provider being there.
 */

import { createContext, type ReactNode, useContext } from "react";

export interface CodeBlockContext {
	/** The answer is still arriving, so the block may be cut short. */
	streaming: boolean;
	/**
	 * Every block of this language in the same answer, in order, this one
	 * included (DOP-653). A surface that acts on several blocks together
	 * reads them from here; a block is its raw text, as the answer wrote it.
	 */
	siblings: string[];
}

/** The fenced blocks of one language in an answer's text, in order. */
export function fencedBlocks(text: string, language: string): string[] {
	const name = language.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
	const fence = new RegExp(`(^|\\n)\`\`\`+${name}[ \\t]*\\n([\\s\\S]*?)\\n\`\`\`+`, "g");
	return Array.from(text.matchAll(fence), (match) => match[2]);
}

/** Language name to renderer; the renderer gets the block's raw text. */
export type CodeBlockRenderers = Readonly<
	Record<string, (code: string, context: CodeBlockContext) => ReactNode>
>;

export const CodeBlockRenderersContext = createContext<CodeBlockRenderers>({});

export const useCodeBlockRenderers = (): CodeBlockRenderers => useContext(CodeBlockRenderersContext);
